<template>
    <div class="space-y-3">
        <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
                <h2 class="truncate text-base font-semibold text-white">{{ profile.display_name || "No name yet" }}<template v-if="profile.age">, {{ profile.age }}</template></h2>
                <p class="text-xs capitalize text-nomad-200">{{ [profile.gender, profile.city, profile.country].filter(Boolean).join(" · ") }}</p>
                <p v-if="profile.email" class="break-all text-xs text-nomad-200">{{ profile.email }}</p>
            </div>
            <span class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize" :class="statusClass(profile.status)">{{ profile.status }}</span>
        </div>

        <div v-if="badges.length" class="flex flex-wrap gap-1">
            <span v-for="badge in badges" :key="badge" class="rounded-full bg-luuqa/15 px-2 py-0.5 text-[11px] font-medium text-luuqa">{{ badge }}</span>
        </div>

        <p v-if="genderWarning" class="rounded-md bg-luuqa/15 p-2 text-xs font-medium text-luuqa">{{ genderWarning }}</p>

        <p v-if="profile.bio" class="whitespace-pre-line text-[13px] leading-5 text-nomad-50">{{ profile.bio }}</p>

        <p class="text-xs text-nomad-200"><span class="font-semibold text-white">Education:</span> {{ profile.education?.length ? profile.education.join(" · ") : "Not added" }}</p>

        <div class="flex items-center gap-2.5 rounded-md bg-nomad-800 p-2">
            <ArtworkImage v-if="profile.verification?.url" :src="profile.verification.url" title="Verification photo" class="h-14 w-11 shrink-0 rounded bg-nomad-700" />
            <div class="min-w-0 flex-1">
                <p class="text-xs font-semibold text-white">Verification photo</p>
                <p v-if="profile.verification?.pose && !profile.verification.verified" class="text-[11px] font-semibold text-luuqa">Asked to: {{ profile.verification.pose }}</p>
                <p class="text-[11px] text-nomad-200">{{ verificationNote }}</p>
                <p class="text-[11px] text-nomad-200">{{ activity }}</p>
            </div>
            <span v-if="profile.verification?.verified" class="shrink-0 rounded-full bg-luuqa/15 px-2 py-0.5 text-[11px] font-medium text-luuqa">Verified</span>
        </div>

        <p v-if="!profile.photos.length" class="text-xs text-nomad-200">No photos.</p>
        <div v-else class="grid grid-cols-3 gap-1.5">
            <div v-for="photo in profile.photos" :key="photo.id" class="relative aspect-[3/4]">
                <ArtworkImage :src="photo.url" :title="profile.display_name || 'Photo'" class="h-full w-full rounded-md bg-nomad-800" />
                <span v-if="photo.status !== 'approved'" class="pointer-events-none absolute left-1 top-1 rounded bg-luuqa px-1 text-[10px] font-semibold text-white">New</span>
                <button type="button" aria-label="Remove photo"
                    class="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded bg-nomad-900/80 text-[11px] text-white hover:bg-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-luuqa"
                    @click="$emit('remove-photo', photo)">
                    <span class="icon-x" aria-hidden="true"></span>
                </button>
            </div>
        </div>

        <slot></slot>
    </div>
</template>
<script>
import ArtworkImage from "@/components/ui/ArtworkImage.vue";

export const statusClass = (status) => ({
    approved: "bg-luuqa/15 text-luuqa",
    pending: "bg-yellow-500/15 text-yellow-400",
    rejected: "bg-red-500/15 text-red-400",
    suspended: "bg-red-500/15 text-red-400",
}[status] ?? "bg-nomad-800 text-nomad-100");

export default {
    name: "LuuqaProfileDetail",
    components: { ArtworkImage },
    props: {
        profile: { type: Object, required: true },
    },
    emits: ["remove-photo"],
    computed: {
        badges() {
            const p = this.profile;
            return [
                p.changes_pending ? "Edit waiting" : null,
                p.held ? "Hidden from others" : null,
                p.repeat_opt_outs ? `Opted out ${p.opt_outs} times lately` : null,
            ].filter(Boolean);
        },
        genderWarning() {
            const { gender, gender_change: change, account_gender: account } = this.profile;
            if (change) {
                const matches = change.matches === 1 ? "their 1 match" : `their ${change.matches} matches`;
                return `Asked to change from ${gender} to ${change.to}. Check the verification photo. Approving deletes ${matches}, chats and swipes so they start fresh.`;
            }
            return account && gender ? `This profile says ${gender}, but their Heesify account says ${account}. They may have come back as the other gender.` : null;
        },
        verificationNote() {
            const verification = this.profile.verification;
            if (verification?.requested) return "Requested. Waiting for them to take it.";
            if (verification?.url) return "Check the pose and that it matches their photos. It's deleted when you approve.";
            return verification?.verified ? "Checked by a person, and the photo was deleted." : "None yet.";
        },
        activity() {
            const { photos_taken: photos = 0, changes = 0 } = this.profile.verification ?? {};
            return `${photos} ${photos === 1 ? "photo" : "photos"} taken, ${changes} profile ${changes === 1 ? "change" : "changes"}`;
        },
    },
    methods: { statusClass },
};
</script>
